import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  FunnelIcon,
  UsersIcon,
  CreditCardIcon,
  PauseIcon,
  PlayIcon,
  CircleStackIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useGetAllCompaniesQuery, useDeleteCompanyMutation, useHardDeleteCompanyMutation, useToggleSubscriptionMutation } from '../../store/api/superAdminApi';
import { formatCurrency, formatDate } from '../../utils/formatters';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import Modal from '../../components/common/Modal';
import CompanyDetailsModal from './components/CompanyDetailsModal';
import EditCompanyModal from './components/EditCompanyModal';
import toast from 'react-hot-toast';

const CompanyManagement = () => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [hardDeletePassword, setHardDeletePassword] = useState('');

  const { data, isLoading, error, refetch } = useGetAllCompaniesQuery({
    page,
    limit: 10,
    search,
    status
  });

  const [deleteCompany, { isLoading: isDeleting }] = useDeleteCompanyMutation();
  const [hardDeleteCompany, { isLoading: isHardDeleting }] = useHardDeleteCompanyMutation();
  const [toggleSubscription, { isLoading: isToggling }] = useToggleSubscriptionMutation();

  const companies = data?.data?.companies || [];
  const pagination = data?.data?.pagination || {};

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatusFilter = (e) => {
    setStatus(e.target.value);
    setPage(1);
  };

  const handleViewDetails = (company) => {
    setSelectedCompany(company);
    setShowDetailsModal(true);
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setShowEditModal(true);
  };

  const handleDeleteClick = (company) => {
    setSelectedCompany(company);
    setShowDeleteModal(true);
  };

  const handleHardDeleteClick = (company) => {
    setSelectedCompany(company);
    setShowHardDeleteModal(true);
  };

  const handleSuspendClick = (company) => {
    setSelectedCompany(company);
    setShowSuspendModal(true);
  };

  const handleSuspendConfirm = async () => {
    try {
      await toggleSubscription({ 
        id: selectedCompany._id, 
        reason: suspensionReason || 'Suspended by super admin' 
      }).unwrap();
      const action = selectedCompany.subscription?.status === 'active' ? 'suspended' : 'resumed';
      toast.success(`Subscription ${action} successfully`);
      setShowSuspendModal(false);
      setSelectedCompany(null);
      setSuspensionReason('');
      refetch();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteCompany(selectedCompany._id).unwrap();
      toast.success('Company deactivated successfully');
      setShowDeleteModal(false);
      setSelectedCompany(null);
      refetch();
    } catch (error) {
      toast.error('Failed to deactivate company');
    }
  };

  const handleHardDeleteConfirm = async () => {
    if (!hardDeletePassword.trim()) {
      toast.error('Password is required');
      return;
    }

    try {
      await hardDeleteCompany({
        id: selectedCompany._id,
        password: hardDeletePassword
      }).unwrap();
      toast.success('Company and all associated data permanently deleted');
      setShowHardDeleteModal(false);
      setSelectedCompany(null);
      setHardDeletePassword('');
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to delete company');
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Failed to load companies" />;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Company Management</h1>
              <p className="text-gray-600 mt-2">Manage all companies and their subscriptions</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search companies..."
                value={search}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <FunnelIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
              <select
                value={status}
                onChange={handleStatusFilter}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Showing {companies.length} of {pagination.totalRecords} companies
              </span>
            </div>
          </div>
        </div>

        {/* Companies Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employees
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DB Space
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {companies.map((company, index) => (
                  <motion.tr
                    key={company._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-blue-500 flex items-center justify-center">
                            <BuildingOfficeIcon className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{company.name}</div>
                          <div className="text-sm text-gray-500">{company.code}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{company.email}</div>
                      <div className="text-sm text-gray-500">{company.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {company.subscription ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {company.subscription.planId?.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatCurrency(company.subscription.price, company.currency || 'INR')} / {company.subscription.billingCycle}
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            company.subscription.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : company.subscription.status === 'trial'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {company.subscription.status}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No subscription</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UsersIcon className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{company.employeeCount || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CircleStackIcon className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm text-gray-900">
                            {company.dbSpaceUsage?.formattedSize || 'Calculating...'}
                          </div>
                          {company.dbSpaceUsage?.hasError && (
                            <div className="text-xs text-red-500">Error</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        company.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {company.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(company)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditCompany(company)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                          title="Edit Company"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(company)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                          title="Deactivate Company"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleHardDeleteClick(company)}
                          className="text-red-800 hover:text-red-900 p-1 rounded hover:bg-red-100"
                          title="Permanently Delete Company"
                        >
                          <ExclamationTriangleIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination.total > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {pagination.current} of {pagination.total}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.total}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showDetailsModal && (
        <CompanyDetailsModal
          company={selectedCompany}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedCompany(null);
          }}
        />
      )}

      {showEditModal && (
        <EditCompanyModal
          company={selectedCompany}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCompany(null);
          }}
          onSuccess={() => {
            refetch();
            setShowEditModal(false);
            setSelectedCompany(null);
          }}
        />
      )}

      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Deactivate Company"
        >
          <div className="p-6">
            <p className="text-gray-600 mb-6">
              Are you sure you want to deactivate <strong>{selectedCompany?.name}</strong>? 
              This will also deactivate all employees and cancel their subscription.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showHardDeleteModal && (
        <Modal
          isOpen={showHardDeleteModal}
          onClose={() => {
            setShowHardDeleteModal(false);
            setHardDeletePassword('');
          }}
          title="Permanently Delete Company"
        >
          <div className="p-6">
            <div className="flex items-start space-x-3 mb-6">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  This action cannot be undone
                </h3>
                <p className="text-gray-600 mb-4">
                  You are about to permanently delete <strong>{selectedCompany?.name}</strong> and all associated data including:
                </p>
                <ul className="list-disc list-inside text-gray-600 mb-4 space-y-1">
                  <li>All employees and their records</li>
                  <li>All payroll and salary data</li>
                  <li>All leave and attendance records</li>
                  <li>All subscriptions and payment history</li>
                  <li>All company settings and configurations</li>
                </ul>
                <p className="text-red-600 font-medium">
                  This will completely remove the company from the system.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="hardDeletePassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm with your password
              </label>
              <div className="relative">
                <LockClosedIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="password"
                  id="hardDeletePassword"
                  value={hardDeletePassword}
                  onChange={(e) => setHardDeletePassword(e.target.value)}
                  placeholder="Enter your super admin password"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowHardDeleteModal(false);
                  setHardDeletePassword('');
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleHardDeleteConfirm}
                disabled={isHardDeleting || !hardDeletePassword.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isHardDeleting ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CompanyManagement;